define([
	"underscore",
	"csi/core/extendedPathService",
	"csi/reflection",
	"csi/middleware/rollup/rollupService",
	"csi/store/UserNames",
	"csi/xml"
], function(_, {convertToStandardPath}, reflection, {evaluate}, UserNames, xml) {

	// server currently supports both Id and ID, so client must as well
	var FILTER_PROP_ID2 = "ID";

	var _containerOperators = {
		"EX": _exists,
		"NX": _notExists,
		"NL": _isNull,
		"NN": _isNotNull,
		"US": _isEmptyContainer,
		"UE": _isEmptyContainer,
		"UL": _isEmptyNameContainer,
		"UT": _isEmptyContainer
	};

	var _sortTags = {
		"OrderByAttribute": 1,
		"OrderByNode": 1
	};

	var _filterFunctions;
	var _opFunctions;
	var _sortValuesByUrl = {};
	var _regExps = {};

	function _getPathNodes(/*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
		// summary: get nodes along path specified in filter definition
		var path = xn.getAttribute(xml.FILTER_PROP_PATH);
		if (!path) {
			return [node];
		}

		var bSingle = true;

		function _onPathStep(nodeStart, nodesPrevious, nodesNext, idRelation, bChild, index) {
			// track if step is relation to single node or diverges into matrix;
			// assume that if relation is missing then it is single
			if (bSingle && (index == null)) {
				var rca = reflection.getAttribute(nodesPrevious[0], idRelation);
				bSingle = !rca || rca.isSingle;
			}
		}

		var pathNodes = cache.getPathNodes(node, path, null, false, _onPathStep);

		// if path is for single node and is not filled, then return null
		if (bSingle && (!pathNodes || !pathNodes.length)) {
			return [null];
		}

		// otherwise return result which could be many nodes in matrix
		return pathNodes || [];
	}

	function _getRegExp(/*String*/value) {
		// summary: convert filter value into regular expression
		return _regExps[value] = _regExps[value] || xml.getRegExpForMatchValue(value);
	}

	function _getSortObject(/*XMLNode*/xn) {
		// summary: convert XML sort definition to object that will be applied later
		var id = xn.getAttribute(xml.FILTER_PROP_ID);
		var index = id ? xn.getAttribute(xml.FILTER_PROP_INDEX) : null;
		var prop = id ? null : xml.filterParameterToProperty(xn.getAttribute(xml.FILTER_PROP_PARAMETER));
		var f;

		if (index != null) {
			f = function(node) {
				var value = node[id];
				return value ? value[index] : undefined;
			};
		} else if (id) {
			f = function(node) {
				return node[id];
			};
		} else {
			f = function(node) {
				var value = xml.nodeGetProperty(node, prop);

				if (xml.propertyType(prop) == xml.ATTR_REF) {
					value = UserNames.get(value);
				}

				return value;
			};
		}

		return {
			descending: (xn.getAttribute(xml.SORT_SEQUENCE) == xml.SORT_DESC),
			path: xn.getAttribute(xml.FILTER_PROP_PATH),
			valueFunction: f
		};
	}

	function _getSortValue(/*Object*/sortObject, /*Number*/index, /*Node*/node, /*Cache*/cache) {
		// summary: return sort value for given node and sort definition - cache result for quick access next time
		var url = node.$URL;
		var a = _sortValuesByUrl[url];
		if (!a) {
			a = _sortValuesByUrl[url] = [];
		}

		if (a.length > index) {
			return a[index];
		}

		var pathNode = sortObject.path ? cache.getPathNode(node, sortObject.path) : node;
		var v;

		if (pathNode) {
			v = sortObject.valueFunction(pathNode);

			// force null values to undefined to keep sorting consistent
			if (v === null) {
				v = undefined;
			}
		}

		// force to lowercase since sorting is case-insensitive
		if (v && v.toLowerCase) {
			v = v.toLowerCase();
		}

		a.push(v);
		return v;
	}

	function _evaluateExpressions(/*XMLNode*/xnRoot, /*Node*/node, /*Cache*/cache) {
		if (xnRoot.childNodes.length) {
			// convert to array so that don't experience problems if child removed from xnRoot as below
			_.each(_.toArray(xnRoot.childNodes), function(xn) {
				_evaluateExpressions(xn, node, cache);
			});
		} else if (xnRoot.getAttribute) {
			// if value is expression, then calculate and replace value with result;
			// be careful to skip text nodes that could come from newline characters in view XML
			var prop = xml.filterGetValueProperty(xnRoot);
			var value = xnRoot.getAttribute(prop);
			if (value != null) {
				var result = evaluate(value, node, cache, true);
				if (result == null) {
					// do not replace value with null if expression did not evaluate correctly
				} else if (!_.isNumber(result) || (Number(result) != value)) {
					// leave value alone in special case where decimal numbers (5.0) are turned into integers (5)
					xnRoot.setAttribute(prop, result);
				}
			}
		}
	}

	function _isBlank(op, pathNodes) {
		return !pathNodes.length && ((op == xml.OP_NULL) || (op.charAt(0) == xml.OP_BLANKS));
	}

	function _lower(v) {
		return (v && v.toLowerCase) ? v.toLowerCase() : v;
	}

	function _sortComparison(v1, v2) {
		// assumption is that this only works with values of same type (number, string, boolean), not with values of various types
		if (v1 === undefined) {
			return -1;
		} else if (v2 === undefined) {
			return 1;
		}

		return (v2 < v1) - (v1 < v2);
	}

	function _applyAccess(/*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
		var pathNodes = _getPathNodes(xn, node, cache);
		return (pathNodes[0] != null);
	}

	function _applyAttribute(/*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
		var pathNodes = _getPathNodes(xn, node, cache);
		var op = xn.getAttribute(xml.FILTER_PROP_OP);

		// if path leads to no nodes (empty matrix), and operator is looking for blanks, then return true
		if (_isBlank(op, pathNodes)) {
			return true;
		}

		var dim = xn.getAttribute(xml.FILTER_PROP_DIMENSION);
		var flag = xn.getAttribute(xml.FILTER_PROP_FLAG);
		var id = xn.getAttribute(xml.FILTER_PROP_ID) || xn.getAttribute(FILTER_PROP_ID2);
		var nodeValues;
		var v2;

		if (dim != null) {
			// comparing size of attribute value
			v2 = Number(dim);

			nodeValues = _.map(pathNodes, function(pn) {
				return pn ? _.size(pn[id]) : undefined;
			});
		} else if (flag != null) {
			// comparing flags
			v2 = Number(flag);

			nodeValues = _.map(pathNodes, function(pn) {
				return pn ? xml.nodeAttributeGetFlags(pn, id) : undefined;
			});
		} else {
			console.assert(!!id);

			// comparing attribute value - use first node to get type of attribute
			var index = xn.getAttribute(xml.FILTER_PROP_INDEX);
			var type = xml.nodeAttributeGetType(pathNodes[0], id);
			var baseType = xml.attributeTypeGetBase(type) || type;
			var prop = xml.filterGetValueProperty(xn);

			v2 = xml.filterGetValue(xn, prop);

			if (op == xml.OP_MATCHES) {
				v2 = _getRegExp(v2);
			}

			// make sure filter value for boolean attributes is a Boolean rather than String - explicitly test for true/false in cases where filtering across multiple value types
			if (baseType == xml.ATTR_BOOLEAN) {
				if (v2 == "true") {
					v2 = true;
				} else if (v2 == "false") {
					v2 = false;
				}
			}

			nodeValues = _.map(pathNodes, function(pn) {
				var v1 = pn ? pn[id] : undefined;

				// handle container values and index
				if (pn && (index != null)) {
					v1 = v1 ? v1[index] : undefined;

					// handle localized name filtering specially - if LocalizedName has value for index, then test it; otherwise, test name below
					if (!v1 && (prop == xml.FILTER_NVALUE)) {
						v1 = pn.$Name;
					}
				}

				return v1;
			});
		}

		// return true if any nodes in path pass filter
		return _applyOperator(op, pathNodes, nodeValues, v2);
	}

	function _applyNode(/*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
		var pathNodes = _getPathNodes(xn, node, cache);
		var op = xn.getAttribute(xml.FILTER_PROP_OP);

		// if path leads to no nodes (empty matrix), and operator is looking for blanks, then return true
		if (_isBlank(op, pathNodes)) {
			return true;
		}

		var prop = xml.filterParameterToProperty(xn.getAttribute(xml.FILTER_PROP_PARAMETER));
		var type = xml.propertyType(prop);
		var bRef = (type == xml.ATTR_REF);
		var v2 = xn.getAttribute(xml.FILTER_VALUE);

		if (op == xml.OP_MATCHES) {
			v2 = _getRegExp(v2);
		}

		var nodeValues = _.map(pathNodes, function(pn) {
			var v1 = xml.nodeGetProperty(pn, prop);
			if (v1 && bRef) {
				v1 = UserNames.get(v1);
			}

			return v1;
		});

		return _applyOperator(op, pathNodes, nodeValues, v2);
	}

	function _applyOperator(op, pathNodes, nodeValues, v2) {
		var operator = _opFunctions[op];
		var valueFunction = (op == xml.OP_MATCHES) ? _lower : _.identity;

		// filter comparisons are case-insensitive
		v2 = valueFunction(v2);

		return _.some(nodeValues, function(v1, i) {
			var pn = pathNodes[i];

			if ((v1 != null) && (_.isArray(v1) || _.isObject(v1))) {
				// return true if operator can apply to whole container and function passes
				if (_containerOperators[op]) {
					return _containerOperators[op](v1, v2, pn, operator);
				}

				// return true if one element in object or array matches query
				return _.some(v1, function(v) {
					return operator(valueFunction(v), v2, pn);
				});
			}

			return operator(valueFunction(v1), v2, pn);
		});
	}

	function _applyAnd(/*XMLNode*/xnRoot, /*Node*/node, /*Cache*/cache) {
		return _.every(xnRoot.childNodes, function(xn) {
			return _applyFilter(xn, node, cache);
		});
	}

	function _applyOr(/*XMLNode*/xnRoot, /*Node*/node, /*Cache*/cache) {
		var xns = xnRoot.childNodes;

		return !xns.length || _.some(xns, function(xn) {
			return _applyFilter(xn, node, cache);
		});
	}

	function _applyNot(/*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
		return !_applyAnd(xn, node, cache);
	}

	function _applyFilter(/*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
		// skip sorting while filtering
		var f = _filterFunctions[xn.tagName];
		return f ? f(xn, node, cache) : true;
	}

	// operator functions
	//		v1 - node instance value
	//		v2 - filter value (in case of matches, the filter value is actually a regular expression)
	function _exists(v1, v2) {return v1 !== undefined;}
	function _notExists(v1, v2) {return v1 === undefined;}

	function _matches(v1, v2) {return v1 != null && v2.test(v1);}
	function _equals(v1, v2) {return v1 != null && v1 == v2;}
	function _notEquals(v1, v2) {return v1 != null && v1 != v2;}
	function _lessThan(v1, v2) {return v1 != null && v1 < v2;}
	function _lessThanOrEqual(v1, v2) {return v1 != null && v1 <= v2;}
	function _greaterThan(v1, v2) {return v1 != null && v1 > v2;}
	function _greaterThanOrEqual(v1, v2) {return v1 != null && v1 >= v2;}

	function _doesNotHaveFlag(v1, v2) {return v1 != null && (v1 & v2) == 0;}
	function _hasFlag(v1, v2) {return v1 != null && (v1 & v2) != 0;}
	function _hasAllFlags(v1, v2) {return v1 != null && (v1 & v2) == v2;}

	function _isNull(v1, v2) {return (v1 == null) || (_.isObject(v1) && _.isEmpty(v1));}
	function _isNotNull(v1, v2) {return (v1 != null) && (!_.isObject(v1) || !_.isEmpty(v1));}

	function _isFilledDefault(v1, v2) {return v1 || (v1 === 0) || (v1 === false);}
	function _isFilledEnum(v1, v2) {return !!xml.enumGetText(v1);}
	function _isFilledName(v1, v2, node) {return !!(v1 || (node && node.$Name));}
	function _isFilledTime(v1, v2) {return (v1 != null && v1 != 0);}
	function _isEmptyDefault(v1, v2) {return !v1 && (v1 !== 0) && (v1 !== false);}
	function _isEmptyEnum(v1, v2) {return !xml.enumGetText(v1);}
	function _isEmptyName(v1, v2, node) {return !v1 && (!node || !node.$Name);}
	function _isEmptyNameContainer(v1, v2, node, operator) {
		// EmptyName has unique logic. It checks value AND the node name, so we need a separate method for this.
		return _isEmptyContainer(v1, null, node, operator) && (!node || !node.$Name);
	}
	function _isEmptyTime(v1, v2) {return (v1 == null || v1 === 0);}
	function _isEmptyContainer(v1, v2, node, operator) {
		return _.isEmpty(v1) || _.every(v1, function(v) {
			return operator(v, v2, node);
		});
	}

	_filterFunctions = {
		"Access": _applyAccess,
		"Attribute": _applyAttribute,
		"Node": _applyNode,
		"Query": _applyAnd,
		"AND": _applyAnd,
		"OR": _applyOr,
		"NOT": _applyNot
	};

	_opFunctions = {
		"EX": _exists,
		"NX": _notExists,
		"RE": _matches,
		"EQ": _equals,
		"NE": _notEquals,
		"LT": _lessThan,
		"LE": _lessThanOrEqual,
		"GT": _greaterThan,
		"GE": _greaterThanOrEqual,
		"NL": _isNull,
		"NN": _isNotNull,
		"B0": _doesNotHaveFlag,
		"B1": _hasFlag,
		"BA": _hasAllFlags,
		"FS": _isFilledDefault,
		"FE": _isFilledEnum,
		"FL": _isFilledName,
		"FT": _isFilledTime,
		"US": _isEmptyDefault,
		"UE": _isEmptyEnum,
		"UL": _isEmptyName,
		"UT": _isEmptyTime
	};

	return {
		addFilterToQueryObject: function(/*Object*/queryObject, /*XMLNode*/xn, /*Node*/node, /*Cache*/cache) {
			// summary: add additional filter to query object
			var value = xml.filterGetValue(xn);
			var result = evaluate(value, node, cache, true);
			if (result != null) {
				var id = xn.getAttribute(xml.FILTER_PROP_ID);
				var prop = xn.getAttribute(xml.FILTER_PROP_PARAMETER);
				var path = convertToStandardPath(xn.getAttribute(xml.FILTER_PROP_PATH));
				var queryKey = xml.queryKey(id, null, path, prop);

				queryObject[queryKey] = [result];
			}
		},

		applyQueryToNodes: function(/*Query*/q, /*Node[]*/nodes, /*Cache*/cache) {
			// summary: filter and sort given nodes by query
			if (q.isEmpty()) {
				return nodes;
			}

			// filter nodes
			nodes = _.filter(nodes, function(node) {
				return _applyFilter(q.element, node, cache);
			});

			// find OrderBy definitions
			var sortObjects = [];
			var n = 0;

			_.each(q.element.childNodes, function(xn) {
				if (_sortTags[xn.tagName]) {
					sortObjects.push(_getSortObject(xn));
					++n;
				}
			});

			if (n) {
				// sort nodes if query contains OrderBy definitions
				nodes.sort(function(node1, node2) {
					var sortObject;
					var v1;
					var v2;
					var i;

					for (i = 0; i < n; ++i) {
						sortObject = sortObjects[i];
						v1 = _getSortValue(sortObject, i, node1, cache);
						v2 = _getSortValue(sortObject, i, node2, cache);

						if (v1 !== v2) {
							return sortObject.descending ? _sortComparison(v2, v1) : _sortComparison(v1, v2);
						}
					}

					return 0;
				});

				// match server functionality by removing duplicates while sorting
				nodes = _.uniq(nodes, true, xml.nodeGetURL);
			}

			// reset private members supporting sorting and filtering for next time
			_regExps = {};
			_sortValuesByUrl = {};

			return nodes;
		},

		evaluateQueryExpressions: function(/*Query*/q, /*Node*/node, /*Cache*/cache) {
			// summary: search for attributes with expressions and evaluate - if error, then disregard filter
			_evaluateExpressions(q.element, node, cache);
		}
	};
});